/**
 * Feature Platform — Client SDK
 * --------------------------------
 * Provides:
 *   - <FeatureProvider>            mounts once at the app root (no extra polling)
 *   - useFeatureFlag(key, def)     — boolean / variant value
 *   - useRemoteConfig(key, def)    — typed config value
 *   - <FeatureGate flag="..." fallback={...}>
 *   - getExperimentVariant(key)    — async; persists assignment server-side
 *   - recordExperimentConversion(key)
 *   - refreshFeatures()            — manual refresh (e.g. after admin saves)
 *
 * Design rules:
 *   - Stale-while-revalidate. Reads are *never* blocked on the network.
 *   - localStorage acts as a warm cache so the very first paint after launch
 *     already has the last-known flag values.
 *   - All callers receive a fallback default, so the app *cannot* hard-crash
 *     if `/api/features` 5xxs.
 *   - Lightweight polling: every 5 minutes when tab visible, paused when blurred.
 *     A `duolyst:features-refresh` window event triggers an instant refetch
 *     (used by the admin UI after toggling a kill switch).
 */
import {
  createContext, useContext, useEffect, useState, useRef, useCallback, useMemo,
} from "react";
import axios from "axios";
import { API } from "@/App";

const STORAGE_KEY = "duolyst_features_v1";
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;        // 5 minutes when visible
const REFRESH_EVENT = "duolyst:features-refresh"; // dispatch to force refresh

const FeatureContext = createContext({
  flags: {},
  config: {},
  ready: false,
  error: null,
  refresh: () => {},
});

function readWarmCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeWarmCache(snapshot) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* quota / private mode — ignore */
  }
}

/**
 * Mount once near the root of the app. Cheap, fault-tolerant.
 */
export function FeatureProvider({ children }) {
  const cached = useMemo(readWarmCache, []);
  const [flags, setFlags] = useState(cached?.flags || {});
  const [config, setConfig] = useState(cached?.config || {});
  const [ready, setReady] = useState(Boolean(cached));
  const [error, setError] = useState(null);
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      // We call the two endpoints in parallel; either failure is non-fatal
      // because we still have whatever was in the warm cache.
      const [flagsRes, cfgRes] = await Promise.allSettled([
        axios.get(`${API}/features`, { timeout: 8000 }),
        axios.get(`${API}/features/config`, { timeout: 8000 }),
      ]);
      const newFlags = flagsRes.status === "fulfilled"
        ? (flagsRes.value.data?.flags || {})
        : flags;
      const newConfig = cfgRes.status === "fulfilled"
        ? (cfgRes.value.data?.config || {})
        : config;
      setFlags(newFlags);
      setConfig(newConfig);
      setReady(true);
      setError(null);
      writeWarmCache({ flags: newFlags, config: newConfig, t: Date.now() });
    } catch (e) {
      // Total failure — keep last-known values, surface error for diagnostics
      setError(e?.message || "feature_fetch_failed");
    } finally {
      inFlight.current = false;
    }
  }, [flags, config]);

  // Initial fetch + periodic refresh + visibility-aware
  useEffect(() => {
    let timer = null;
    const tick = () => {
      if (typeof document === "undefined" || !document.hidden) {
        refresh();
      }
    };
    tick(); // initial fetch (stale-while-revalidate)
    timer = setInterval(tick, REFRESH_INTERVAL_MS);

    const onVisibility = () => {
      if (!document.hidden) refresh();
    };
    const onForce = () => refresh();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener(REFRESH_EVENT, onForce);

    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener(REFRESH_EVENT, onForce);
    };
    // We only want this once on mount. `refresh` reads ref values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ctx = useMemo(() => ({ flags, config, ready, error, refresh }),
    [flags, config, ready, error, refresh]);
  return <FeatureContext.Provider value={ctx}>{children}</FeatureContext.Provider>;
}

/**
 * useFeatureFlag(key, defaultValue?)
 *
 * Returns the resolved flag value (boolean OR the multivariate variant value).
 * `defaultValue` is what gets returned while the SDK is still warming up OR
 * when the API has never succeeded for this user.
 *
 * NEVER throws.
 */
export function useFeatureFlag(key, defaultValue = false) {
  const { flags, ready } = useContext(FeatureContext);
  if (!key) return defaultValue;
  if (!ready) return flags[key] ?? defaultValue; // warm-cache hit OR default
  // Treat missing keys as default (safe, never crashes the UI)
  return key in flags ? flags[key] : defaultValue;
}

/**
 * useRemoteConfig(key, defaultValue?)
 *
 * Returns the typed remote config value. Same fallback semantics as flags.
 */
export function useRemoteConfig(key, defaultValue = null) {
  const { config, ready } = useContext(FeatureContext);
  if (!key) return defaultValue;
  if (!ready) return config[key] ?? defaultValue;
  return key in config ? config[key] : defaultValue;
}

/**
 * <FeatureGate flag="globe_projection" fallback={null}>
 *   <GlobeButton />
 * </FeatureGate>
 *
 * Renders children only if the flag resolves truthy.
 */
export function FeatureGate({ flag, fallback = null, children }) {
  const value = useFeatureFlag(flag, false);
  if (!value) return fallback;
  return children;
}

/**
 * Force the SDK to re-fetch right now (e.g., the admin saved a flag).
 */
export function refreshFeatures() {
  try {
    window.dispatchEvent(new Event(REFRESH_EVENT));
  } catch {
    /* SSR — ignore */
  }
}

// ── Experiments ────────────────────────────────────────────────────────────

const _expCache = new Map(); // experiment_key -> assigned variant (per-tab)

export async function getExperimentVariant(experimentKey) {
  if (!experimentKey) return null;
  if (_expCache.has(experimentKey)) return _expCache.get(experimentKey);
  // localStorage assignment cache (sticky across reloads for same user)
  try {
    const stored = localStorage.getItem(`duolyst_exp_${experimentKey}`);
    if (stored) {
      _expCache.set(experimentKey, stored);
      return stored;
    }
  } catch { /* ignore */ }
  try {
    const res = await axios.post(`${API}/features/experiment-impression`,
      { experiment_key: experimentKey }, { timeout: 8000 });
    const variant = res.data?.variant || null;
    if (variant) {
      _expCache.set(experimentKey, variant);
      try { localStorage.setItem(`duolyst_exp_${experimentKey}`, variant); }
      catch { /* ignore */ }
    }
    return variant;
  } catch {
    return null;  // resilient: experiments NEVER block UX
  }
}

export async function recordExperimentConversion(experimentKey) {
  if (!experimentKey) return false;
  try {
    const res = await axios.post(`${API}/features/experiment-conversion`,
      { experiment_key: experimentKey }, { timeout: 8000 });
    return Boolean(res.data?.converted);
  } catch {
    return false;
  }
}
